/**
 * SavedAudienceSourcesPage.jsx — Saved BYO-webhook URL presets CRUD.
 * Marketing reuses these across many BYO campaigns.
 *
 * Backend: /campaigns/whatsapp/audience/presets/(list|create|update|delete)
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Table,
  Tag,
  Button,
  Space,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Popconfirm,
  message as antdMessage,
  Empty,
  Checkbox,
  Typography,
  Grid,
  Skeleton,
} from 'antd';
import { Skeleton as BoneSkeleton } from 'boneyard-js/react';
import {
  PlusOutlined,
  ReloadOutlined,
  EditOutlined,
  DeleteOutlined,
  ApiOutlined,
  WarningOutlined,
  CopyOutlined,
  CheckOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import audienceSourceApi from '../../../../../api/audienceSource.api';
import { useFeatureFlag, FEATURE_KEYS } from '../../../../../utils/planFeatures';

const { useBreakpoint } = Grid;
const { Text } = Typography;

const TableRowSkeleton = () => (
  <div style={{ padding: '4px 0' }}>
    {[...Array(5)].map((_, i) => (
      <div key={i} style={{ display: 'flex', gap: 12, padding: '6px 0', borderBottom: '1px solid #f0f0f0', alignItems: 'center' }}>
        <Skeleton.Input active size="small" style={{ width: 140, height: 14 }} />
        <Skeleton.Input active size="small" style={{ width: 200, height: 14 }} />
        <Skeleton.Input active size="small" style={{ width: 80, height: 14 }} />
        <Skeleton.Input active size="small" style={{ width: 60, height: 14 }} />
        <Skeleton.Input active size="small" style={{ width: 90, height: 14 }} />
        <Skeleton.Input active size="small" style={{ width: 70, height: 14 }} />
      </div>
    ))}
  </div>
);

export default function SavedAudienceSourcesPage() {
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const { enabled: featureEnabled, loading: featureLoading } = useFeatureFlag(FEATURE_KEYS.BYO_WEBHOOK);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();

  // Signing secret — shown once after create or rotate
  const [secretModalOpen, setSecretModalOpen] = useState(false);
  const [revealedSecret, setRevealedSecret] = useState(null);
  const [copiedSecret, setCopiedSecret] = useState(false);
  const [secretConfirmed, setSecretConfirmed] = useState(false);

  const copyToClipboard = async (text) => {
    try { await navigator.clipboard.writeText(text); }
    catch {
      const ta = document.createElement('textarea');
      ta.value = text; document.body.appendChild(ta); ta.select();
      document.execCommand('copy'); document.body.removeChild(ta);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await audienceSourceApi.listPresets();
      // Handle { success, data: [] } and legacy { entity: { presets: [] } } envelopes
      const body = res.data?.data || res.data?.entity || res.data;
      const list = body?.presets || body?.items || (Array.isArray(body) ? body : []);
      setRows(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error('Failed to load presets', err);
      antdMessage.error('Could not load saved audience sources.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (featureEnabled) fetchData(); }, [featureEnabled]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ auth_type: 'none', hard_send_cap: 10000 });
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    form.setFieldsValue({
      name: row.name,
      url: row.url,
      auth_type: row.auth?.type || 'none',
      auth_token: '',         // never pre-fill secrets
      auth_header_name: row.auth?.header_name || '',
      hard_send_cap: row.hard_send_cap || 10000,
    });
    setModalOpen(true);
  };

  const buildPayload = (values) => {
    const auth = { type: values.auth_type || 'none' };
    if (values.auth_type === 'bearer' && values.auth_token)
      auth.token = values.auth_token;
    if (values.auth_type === 'header') {
      if (values.auth_header_name) auth.header_name = values.auth_header_name;
      if (values.auth_token)       auth.token = values.auth_token;
    }
    return {
      name: values.name,
      url: values.url,
      auth,
      hard_send_cap: values.hard_send_cap,
    };
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const payload = buildPayload(values);
      if (editing) {
        await audienceSourceApi.updatePreset(editing.preset_id ?? editing.id, payload);
        antdMessage.success('Preset updated.');
        setModalOpen(false);
        fetchData();
      } else {
        const res = await audienceSourceApi.createPreset(payload);
        const entity = res.data?.data || res.data?.entity || res.data;
        const signingSecret = entity?.signing_secret || res.data?.signing_secret;
        setModalOpen(false);
        fetchData();
        if (signingSecret) {
          setRevealedSecret(signingSecret);
          setSecretConfirmed(false);
          setCopiedSecret(false);
          setSecretModalOpen(true);
        } else {
          antdMessage.success('Preset created.');
        }
      }
    } catch (err) {
      if (err?.errorFields) return;
      antdMessage.error(err.response?.data?.message || 'Save failed.');
    }
  };

  const handleDelete = async (row) => {
    try {
      await audienceSourceApi.deletePreset(row.preset_id ?? row.id);
      antdMessage.success('Preset deleted.');
      fetchData();
    } catch (err) {
      antdMessage.error(err.response?.data?.message || 'Delete failed.');
    }
  };

  if (!featureLoading && !featureEnabled) {
    return (
      <div style={{ padding: 24 }}>
        <Card>
          <Empty
            image={<ApiOutlined style={{ fontSize: 64, color: '#9ca3af' }} />}
            description={
              <div>
                <h3 style={{ margin: '8px 0' }}>BYO Audience not enabled</h3>
                <p style={{ color: '#6b7280' }}>
                  Upgrade your plan to stream contacts from your own server when sending WhatsApp campaigns.
                </p>
              </div>
            }
          >
            <Button type="primary" style={{ background: '#0036b0', borderColor: '#0036b0' }} onClick={() => navigate('/dashboard/pricing')}>View Plans</Button>
          </Empty>
        </Card>
      </div>
    );
  }

  const columns = [
    { title: 'Name', dataIndex: 'name', key: 'name', width: 220 },
    {
      title: 'URL',
      dataIndex: 'url',
      key: 'url',
      ellipsis: true,
      render: (u) => <code style={{ fontSize: 12 }}>{u}</code>,
    },
    {
      title: 'Auth',
      dataIndex: 'auth',
      key: 'auth',
      width: 110,
      render: (a) => <Tag>{a?.type || 'none'}</Tag>,
    },
    {
      title: 'Hard Cap',
      dataIndex: 'hard_send_cap',
      key: 'hard_send_cap',
      width: 110,
      render: (n) => (n || 0).toLocaleString(),
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 130,
      render: (v) => v ? dayjs(v).format('DD MMM YYYY') : '—',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 140,
      fixed: 'right',
      render: (_, row) => (
        <Space size={4}>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(row)} />
          <Popconfirm title="Delete this preset?" onConfirm={() => handleDelete(row)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: screens.xs ? '0 12px 12px' : '0 24px 24px' }}>
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: '1.45rem', fontWeight: 700, color: '#0f172a', margin: '0 0 4px', letterSpacing: '-0.03em', lineHeight: 1.2 }}>
          Audience Sources
        </h2>
        <span style={{ color: '#6b7280', fontSize: 13 }}>Saved BYO-webhook URL presets — reuse across multiple campaigns.</span>
      </div>
      <Card
        styles={{
          header: { minHeight: 40, padding: screens.xs ? '0 12px' : '0 16px' },
          body: { padding: screens.xs ? 8 : 16 },
        }}
        extra={
          <Space size={6}>
            <Button size={screens.xs ? 'small' : 'middle'} icon={<ReloadOutlined />} onClick={fetchData} loading={loading}>Refresh</Button>
            <Button size={screens.xs ? 'small' : 'middle'} type="primary" icon={<PlusOutlined />} onClick={openCreate} style={{ background: '#0036b0', borderColor: '#0036b0' }}>New Preset</Button>
          </Space>
        }
      >
        <BoneSkeleton name="audience-sources" loading={loading && rows.length === 0} fallback={<TableRowSkeleton />}>
          <Table
            rowKey={(r) => r.preset_id || r.id}
            columns={columns}
            dataSource={rows}
            loading={loading && rows.length > 0}
            size="small"
            scroll={{ x: 900 }}
            locale={{
              emptyText: (
                <Empty description="No saved presets yet. Create one to reuse a BYO webhook URL across multiple campaigns." />
              ),
            }}
          />
        </BoneSkeleton>
      </Card>

      <Modal
        title={editing ? 'Edit Preset' : 'Create Preset'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={handleSave}
        okText={editing ? 'Save' : 'Create'}
        okButtonProps={{ style: { background: '#0036b0', borderColor: '#0036b0' } }}
        width={620}
      >
        <Form form={form} layout="vertical" preserve={false}>
          <Form.Item name="name" label="Preset Name" rules={[{ required: true, message: 'Name is required' }]}>
            <Input placeholder="e.g. Diwali Audience Service" />
          </Form.Item>
          <Form.Item
            name="url"
            label="Webhook URL"
            rules={[{ required: true, message: 'URL is required' }, { type: 'url', message: 'Enter a valid URL' }]}
          >
            <Input placeholder="https://your-server.com/api/audiences/diwali" />
          </Form.Item>
          <Form.Item name="auth_type" label="Authentication">
            <Select
              options={[
                { value: 'none',   label: 'No Auth' },
                { value: 'bearer', label: 'Bearer Token' },
                { value: 'header', label: 'Custom Header' },
              ]}
            />
          </Form.Item>
          <Form.Item
            shouldUpdate={(p, c) => p.auth_type !== c.auth_type}
            noStyle
          >
            {({ getFieldValue }) => {
              const t = getFieldValue('auth_type');
              if (t === 'none') return null;
              return (
                <>
                  {t === 'header' && (
                    <Form.Item
                      name="auth_header_name"
                      label="Header Name"
                      rules={[{ required: true, message: 'Header name required' }]}
                      extra='e.g. X-Audience-Secret'
                    >
                      <Input placeholder="X-Audience-Secret" />
                    </Form.Item>
                  )}
                  <Form.Item
                    name="auth_token"
                    label={t === 'bearer' ? 'Bearer Token' : 'Token / Secret Value'}
                    rules={editing ? [] : [{ required: true, message: 'Token is required' }]}
                    extra={editing ? 'Leave blank to keep existing token' : undefined}
                  >
                    <Input.Password placeholder={t === 'bearer' ? 'eyJhbGci...' : 'Secret token value'} />
                  </Form.Item>
                </>
              );
            }}
          </Form.Item>
          <Form.Item
            name="hard_send_cap"
            label="Hard Send Cap"
            extra="Maximum messages this audience source can produce in one campaign."
            rules={[{ required: true, message: 'Cap is required' }]}
          >
            <InputNumber min={1} max={1000000} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

      {/* One-time signing secret reveal — shown after preset creation */}
      <Modal
        open={secretModalOpen}
        title={null}
        footer={null}
        width={460}
        closable={false}
        maskClosable={false}
        destroyOnClose
      >
        <div style={{ textAlign: 'center', padding: '8px 0 12px' }}>
          <WarningOutlined style={{ fontSize: 32, color: '#d48806', display: 'block', marginBottom: 8 }} />
          <h3 style={{ margin: '8px 0 4px', fontSize: 16 }}>Share With Your Webhook Provider</h3>
          <Text style={{ fontSize: 13, color: '#555', lineHeight: 1.6, display: 'block' }}>
            This secret is <strong>not for you</strong> — send it to the team running the webhook
            server. They must save it as an environment variable so their endpoint can verify
            that requests genuinely come from MessageOS.
          </Text>
        </div>
        <div style={{ background: '#fff1f0', border: '1px solid #ffccc7', borderRadius: 8, padding: '12px 16px', fontFamily: 'monospace', fontSize: 13, wordBreak: 'break-all', marginBottom: 8 }}>
          {revealedSecret}
        </div>
        <div style={{ background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 6, padding: '10px 14px', marginBottom: 12, fontSize: 12, lineHeight: 1.7 }}>
          <strong>Tell your webhook provider to:</strong><br />
          1. Store it: <code>MOS_SIGNING_SECRET={revealedSecret}</code><br />
          2. On every incoming POST, verify the <code>X-MOS-Signature</code> header using this secret.<br />
          3. See the <strong>Developer Guide</strong> in API Docs → BYO Campaigns for full verification code.
        </div>
        <Button
          type="primary"
          icon={copiedSecret ? <CheckOutlined /> : <CopyOutlined />}
          block
          size="large"
          style={{ marginBottom: 16, background: copiedSecret ? '#52c41a' : '#0036b0', borderColor: copiedSecret ? '#52c41a' : '#0036b0' }}
          onClick={async () => {
            await copyToClipboard(revealedSecret);
            setCopiedSecret(true);
            antdMessage.success('Signing secret copied');
            setTimeout(() => setCopiedSecret(false), 3000);
          }}
        >
          {copiedSecret ? 'Copied!' : 'Copy Signing Secret'}
        </Button>
        <div style={{ padding: '12px 16px', background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 8, marginBottom: 16 }}>
          <Checkbox checked={secretConfirmed} onChange={(e) => setSecretConfirmed(e.target.checked)}>
            <Text strong style={{ fontSize: 13 }}>I've shared this secret with my webhook provider</Text>
          </Checkbox>
        </div>
        <Button
          type="primary" ghost block
          style={{ borderColor: '#0036b0', color: '#0036b0' }}
          disabled={!secretConfirmed}
          onClick={() => { setSecretModalOpen(false); setRevealedSecret(null); setCopiedSecret(false); setSecretConfirmed(false); }}
        >
          Done
        </Button>
      </Modal>
    </div>
  );
}
